import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Button from '../Button/Button';
import { Add } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ReduxState } from '../../store/types';
import { actions } from '../../store';
import Loader from '../Loader/Loader';
import Sip from './Sip';
import AssignDeviceDialog from '../Devices/AssignDeviceDialog';
import AlertDialog from '../AlertDialog/AlertDialog';
import { DialogButton } from '../AlertDialog/DialogContainer';
import { DeviceProps, generateColumns, useStyles } from './Device.utils';
import classNames from 'classnames';
import PermissionProvider from '../PermissionProvider/PermissionProvider';
import IconWithTooltip from '../Tooltip/IconWithTooltip';
import DataGrid from '../DataGrid/DataGrid';
import { AssignedPortInfo } from '../../store/types/Devices';
import { RealmType } from '../../store/types/CustomerInfo';
import InfoBanner from '../InfoBanner/InfoBanner';
import { showToast } from '../../utils/showToast';

type DeviceDialogState = {
    open: 'none' | 'assign' | 'change' | 'release';
    device?: AssignedPortInfo;
};

// stable reference, so the selector below does not return a fresh array on every render
const EMPTY_DEVICES: AssignedPortInfo[] = [];

// hoisted so its identity survives re-renders - DataGrid feeds it into react-table's getRowId
const getAssignedPortId = (device: AssignedPortInfo, index: number) =>
    `${device.i_ua ?? 'unknown'}-${device.port ?? index}`;

const Device: React.VFC<DeviceProps> = ({
    iAccount,
    register,
    i_c_ext,
    handleDirtyChange,
    handleSetSubmitFunc,
    handleSetIsValidFunc,
    extensionNumber,
    assignPermission,
    releasePermission,
    changePermission,
    sharedLine
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const assignedDevices = useSelector((state: ReduxState) => state.extensions?.device?.items ?? EMPTY_DEVICES);
    const isLoading = useSelector((state: ReduxState) => state.extensions?.device?.isLoading || false);
    const deviceListLoadFailed = useSelector((state: ReduxState) => state.extensions?.device?.loadingFailed || false);
    const apiErrors = useSelector((state:ReduxState) => state.extensions?.errors);

    const [dialog, setDialog] = useState<DeviceDialogState>({ open: 'none' });

    const realm = useSelector<ReduxState, RealmType | undefined>(
        (state) => state.generic?.sessionData?.realm
    );

    const isAccountRealm = realm === RealmType.Account;

    const refreshDeviceList = useCallback(() => {
        dispatch(
            actions.fetchDeviceByIAccount.request({
                i_account: iAccount,
                withDeviceDetails: true
            }),
        );
    }, [dispatch, iAccount]);

    useEffect(() => {
        if (iAccount) {
            refreshDeviceList();
        }
    }, [iAccount, refreshDeviceList]);

    // saga callbacks can come back after the tab is gone, and React 17 warns on those updates
    const isMountedRef = useRef(true);
    useEffect(() => () => {
        isMountedRef.current = false;
    }, []);

    const openAssignDeviceDialog = useCallback(
        () => setDialog({ open: 'assign' }),
        [],
    );
    // the device is kept so its name stays readable while the dialog fades out
    const closeDialog = useCallback(
        () => setDialog((prevState) => ({ ...prevState, open: 'none' })),
        [],
    );

    const showAssignButton = useMemo(() => {
        return !isLoading && !deviceListLoadFailed && !isAccountRealm && (
            sharedLine
            || (!sharedLine && assignedDevices.length < 1)
        )
    }, [isLoading, deviceListLoadFailed, assignedDevices, isAccountRealm]);

    const showNoDeviceBanner = useMemo(() => {
        return !isLoading && !deviceListLoadFailed && isAccountRealm && assignedDevices.length === 0;
    }, [isLoading, deviceListLoadFailed, isAccountRealm, assignedDevices]);

    const handleSubmitReleaseClick = useCallback(() => {
        const id = dialog.device?.i_ua_link;
        if (id) {
            dispatch(
                actions.releaseDevice.request({
                    i_ua_link: id,
                    onSuccess: () => {
                        if (!isMountedRef.current) {
                            return;
                        }

                        closeDialog();
                        showToast(t<string>('screens:devices.deviceReleased'));
                        refreshDeviceList();
                    },
                }),
            );
        }
    }, [closeDialog, dialog, dispatch, refreshDeviceList, t]);

    const actionReleaseDialogButtons = [
        <DialogButton
            key="cancel"
            label={t('common:cancel')}
            onClick={closeDialog}
        />,
        <DialogButton
            key="save"
            label={t('common:release')}
            onClick={handleSubmitReleaseClick}
        />,
    ];

    const handleChangeDeviceClick = useCallback((device: AssignedPortInfo) => {
        setDialog({ open: 'change', device });
    }, []);

    const handleReleaseDeviceClick = useCallback((device: AssignedPortInfo) => {
        setDialog({ open: 'release', device });
    }, []);

    const hideEditAction = (!sharedLine && assignedDevices.length > 1) || isAccountRealm;

    // react-table keeps the column definitions in its own instance,
    // rebuilding them on every render throws that away
    const columns = useMemo(
        () => generateColumns({
            t,
            classes,
            releasePermission,
            changePermission,
            onEdit: handleChangeDeviceClick,
            onDelete: handleReleaseDeviceClick,
            hideEdit: hideEditAction,
            hideRelease: isAccountRealm,
        }),
        [
            t,
            classes,
            releasePermission,
            changePermission,
            handleChangeDeviceClick,
            handleReleaseDeviceClick,
            hideEditAction,
            isAccountRealm
        ],
    );

    return (
        <>
            {isLoading ? (
                <Loader dataQa={'fetch-device-loader'} absolutePosition />
            ) : (
                <div className={classNames(
                    showAssignButton && classes.assignDeviceButtonContainer)}
                >
                    <Grid
                        className={classNames(
                            classes.itemsContainer,
                            classes.assignedDevicesHeader,
                        )}
                    >
                        <div className={classes.titleContainer}>
                            <h5>{t('screens:extensions.assignedDevice')}</h5>
                            <div className={classes.iconContainer}>
                                <IconWithTooltip
                                    dataQa="assigned-devices-tooltip"
                                    tooltipText={t(
                                        'tooltips:extensions.assignedDevice',
                                    )}
                                />
                            </div>
                        </div>
                    </Grid>
                    <Grid
                        className={classNames(
                            classes.itemsContainer,
                            classes.assignedDevicesList,
                        )}
                    >
                        {deviceListLoadFailed ? (
                            <InfoBanner
                                text={t('screens:devices.deviceListLoadFailed')}
                            />
                        ) : showNoDeviceBanner ? (
                            <InfoBanner
                                text={t('screens:devices.noDeviceSelectedForAutoProvisioning')}
                            />
                        ) : (
                            <DataGrid<AssignedPortInfo>
                                classes={{
                                    tableContainer: classes.tableContainer,
                                }}
                                columns={columns}
                                data={assignedDevices}
                                rowCount={assignedDevices.length}
                                pageSizeOptions={[assignedDevices.length]}
                                initialPageSize={Number.MAX_VALUE}
                                centeredRows
                                hideFooter
                                getItemId={getAssignedPortId}
                                dataQa={'assigned-ports-list'}
                                internalHeader
                            />
                        )}
                    </Grid>
                    {showAssignButton && (
                        <PermissionProvider permission={assignPermission || []}>
                            <Button
                                className={classes.assignButton}
                                primary
                                accent
                                icon={<Add />}
                                onClick={openAssignDeviceDialog}
                            >
                                {t('screens:devices.assign')}
                            </Button>
                        </PermissionProvider>
                    )}
                    <AlertDialog
                        isOpen={dialog.open === 'release'}
                        hideHeader={true}
                        description={t('screens:extensions.releaseExtension', {
                            port: dialog.device?.port,
                            name: dialog.device?.device_name,
                            mac: dialog.device?.mac
                        })}
                        dialogActionsButtons={actionReleaseDialogButtons}
                        className={classes.releaseContainer}
                    />
                    {!isAccountRealm && (
                        <Grid
                            className={classNames(
                                classes.itemsContainer,
                                classes.marginTop,
                            )}
                        >
                            <div className={classes.titleContainer}>
                                <h5>{t('screens:extensions.sipInfo')}</h5>
                            </div>
                            <Sip
                                register={register}
                                handleDirtyChange={handleDirtyChange}
                                handleSetSubmitFunc={handleSetSubmitFunc}
                                handleSetIsValidFunc={handleSetIsValidFunc}
                                apiErrors={apiErrors}
                            />
                        </Grid>
                    )}
                    {!isAccountRealm && (
                        <>
                        <PermissionProvider permission={assignPermission || []}>
                            <AssignDeviceDialog
                                id={extensionNumber}
                                i_c_ext={i_c_ext}
                                i_account={iAccount}
                                isOpen={dialog.open === 'assign'}
                                selectedDevice={undefined}
                                onClose={closeDialog}
                                callBackOnSave={() => {
                                    if (!isMountedRef.current) {
                                        return;
                                    }

                                    closeDialog();
                                    showToast(t<string>('screens:devices.deviceAssigned'));
                                    refreshDeviceList();
                                }}
                            />
                        </PermissionProvider>
                        <PermissionProvider permission={changePermission || []}>
                            <AssignDeviceDialog
                                id={extensionNumber}
                                i_c_ext={i_c_ext}
                                i_account={iAccount}
                                isOpen={dialog.open === 'change'}
                                onClose={closeDialog}
                                selectedDevice={
                                    dialog.open === 'change'
                                        ? dialog.device
                                        : undefined
                                }
                                callBackOnSave={() => {
                                    if (!isMountedRef.current) {
                                        return;
                                    }

                                    closeDialog();
                                    showToast(t<string>('screens:devices.deviceChanged'));
                                    refreshDeviceList();
                                }}
                            />
                        </PermissionProvider>
                        </>
                    )}
                </div>
            )}
        </>
    );
};

export default Device;
